import React from "react";
import { t } from "i18next";
import {
  Grid,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  Box,
} from "@mui/material";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { Source } from "@services/sourceManagerApi/models/Source";
import { sourceCard } from "../../../theme/styles";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

export interface SourcesProps {
  loading: boolean;
  sources: Source[];
  isSourceActive: (source: Source) => boolean;
  onDelete: (id: number) => void;
  onOpenRoleModal: (id: number) => void;
}

interface IconContainerProps {
  active: boolean;
  onClick: () => void;
}

const DeleteIconContainer: React.FC<IconContainerProps> = ({ active, onClick }) => (
  <Box
    sx={{
      borderRadius: "50%",
      position: "absolute",
      top: 8,
      right: 8,
      width: 35,
      height: 35,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "&:hover": { cursor: "pointer" },
      backgroundColor: active ? "white" : "transparent",
    }}
    onClick={onClick}
  >
    <DeleteOutlineIcon sx={{ color: active ? "error.main" : "red", fontSize: 30 }} />
  </Box>
);

const ManageIconContainer: React.FC<IconContainerProps> = ({ active, onClick }) => (
  <Box
    sx={{
      p: 2.1,
      borderRadius: "50%",
      position: "absolute",
      top: 8,
      left: 16,
      width: 32,
      height: 32,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "&:hover": { cursor: "pointer" },
    }}
    onClick={onClick}
  >
    <ManageAccountsIcon sx={{ color: active ? "white" : "customColors.mainLower", fontSize: 35 }} />
  </Box>
);

const DocumentIcon: React.FC<{ active: boolean }> = ({ active }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      p: 1,
      my: 2,
      height: "60px",
    }}
  >
    <InsertDriveFileIcon sx={{ fontSize: 54, color: active ? "white" : "#757575" }} />
  </Box>
)

const FileTitle: React.FC<{ name: string; active: boolean }> = ({ name, active }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "3.6em", // Aproximadamente 2 líneas de texto
      textAlign: "center",
      px: 0.5, // Para evitar que el texto toque los bordes
    }}
  >
    <Typography
      variant="h6"
      sx={{
        fontSize: 14,
        fontWeight: 400,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
        color: active ? "white" : "#626262",
        lineHeight: "1.2em",
      }}
      title={name}
    >
      {name}
    </Typography>
  </Box>
);

export const SourcesCard: React.FC<SourcesProps> = ({
    loading,
    sources,
    isSourceActive,
    onDelete,
    onOpenRoleModal,
  }) => {
  if (loading) {
    return (
      <Grid item>
        <CircularProgress size={24} sx={{ mr: 2 }} />
      </Grid>
    );
  }

  if (!loading && sources.length === 0) {
    return (
      <Grid item>
        <Typography component="h2" sx={{ fontSize: 20, fontWeight: 500 }}>
          {t('administration.sources.no_sources_upload')}
        </Typography>
      </Grid>
    );
  }

  return (
    <>
      {sources.map((source) => (
        <Grid item xs={12} sm={6} md={3} key={source.id}>
          <Card sx={sourceCard(isSourceActive(source))}>
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 2,
                position: "relative",
              }}
            >

              <ManageIconContainer
                active={isSourceActive(source)}
                onClick={() => onOpenRoleModal(source.id)}
              />

              <DeleteIconContainer
                active={isSourceActive(source)}
                onClick={() => onDelete(source.id)}
              />

              <DocumentIcon active={isSourceActive(source)} />

              <FileTitle name={source.name} active={isSourceActive(source)} />

            </CardContent>
          </Card>
        </Grid>
      ))}
    </>
  );
};